import { fetchChatStatus } from '@shared/api/chat-status.api'
import type { ChatStatus } from '@shared/api/chat-status.api'
import { logger } from '@shared/lib/logger'
import { attempt } from '@shared/utils'
import { useEffect, useState } from 'react'

interface UseChatStatusResult {
  chatStatus: ChatStatus | null
  isLoading: boolean
}

/**
 * Fetches chat channel status (business hours) from the Global Channel Provider
 * Service.  The request is only made when `enabled` is `true` so callers can
 * gate the fetch on an experiment flag to avoid unnecessary API calls.
 */
export const useChatStatus = (locale: string, enabled: boolean): UseChatStatusResult => {
  const [chatStatus, setChatStatus] = useState<ChatStatus | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!enabled) return

    let cancelled = false

    const load = async (): Promise<void> => {
      setIsLoading(true)

      const { data: status, error } = await attempt(fetchChatStatus(locale))

      if (cancelled) return

      if (error) {
        logger.error('Chat status fetch failed', {
          action: 'chat_status_fetch_failed',
          component: 'useChatStatus',
          error: error.message,
        })
      } else {
        setChatStatus(status)
      }

      setIsLoading(false)
    }

    load()

    return () => {
      cancelled = true
    }
  }, [locale, enabled])

  return { chatStatus, isLoading }
}
